/**
 * TextQuestion Component
 * 
 * Handles text and textarea type questions.
 * Used for:
 * - Introduction challenge question
 * - Final goal question
 * - Any open-ended text input
 * 
 * Features:
 * - Character count with minimum indicator
 * - Real-time validation feedback
 * - Auto-resize for textarea
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TextArea } from '../UI/TextArea';
import { Input } from '../UI/Input';
import { Question } from '../../types/funnel.types';
import { validateField } from '../../utils/validation';

// ============================================
// TYPES
// ============================================

interface TextQuestionProps {
  /** Question data */
  question: Question;
  /** Current answer value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Error message */
  error?: string;
  /** Set error message */
  setError?: (error: string) => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * TextQuestion - Handles text and textarea inputs
 */
export const TextQuestion: React.FC<TextQuestionProps> = ({
  question,
  value,
  onChange,
  error,
  setError,
}) => {
  // Track if user has started typing (for validation timing)
  const [hasInteracted, setHasInteracted] = useState(false);

  // Validate on blur or when value meets requirements
  useEffect(() => {
    if (!hasInteracted) return;
    
    const result = validateField(value, question.validation);
    if (!result.valid && setError) {
      setError(result.message || 'Invalid input');
    } else if (setError) {
      setError('');
    }
  }, [value, hasInteracted, question.validation, setError]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange(e.target.value);
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  // Common props for both input types
  const commonProps = {
    value,
    onChange: handleChange,
    placeholder: question.placeholder,
    error: hasInteracted ? error : undefined,
    required: question.validation?.required,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      {question.type === 'textarea' ? (
        <TextArea
          {...commonProps}
          showCharCount={true}
          minLength={question.validation?.minLength}
          maxLength={question.validation?.maxLength}
          autoResize={true}
          helperText={
            !error && question.validation?.minLength
              ? `Please write at least ${question.validation.minLength} characters`
              : undefined
          }
        />
      ) : (
        <Input
          {...commonProps}
          type="text"
          helperText={
            !error
              ? "Press Enter or click Continue to proceed"
              : undefined
          }
        />
      )}
    </motion.div>
  );
};

TextQuestion.displayName = 'TextQuestion';

export default TextQuestion;
